/**
 * App Profile DTOs - Matching backend ChildNav.Shared/DTOs/AppProfile
 * These DTOs represent the synchronized profile data structure for backend integration
 */

import { AdminStateDto } from '../adminState';
import type { WellCheckDto, GrowthTrackDto, VaccineTrackDto, PrescriptionTrackDto, ReferralTrackDto } from '../../storage/WellCheckStorage';

/**
 * MVar data item for storing question/answer pairs
 * name: mvarname string identifier (e.g., "mvar001", "mvar002")
 * value: data value stored and transported as string
 */
export interface MVarDataDto {
  name: string;
  value: string;
}

/**
 * Admin Action DTO — represents a specific instance of an action defined in ActionDefDto.
 * Links to ActionDefDto via Source, Group, MVar, and Action fields.
 * Maps to the C# AdminActionDto (all fields camelCase on the frontend).
 */
export interface AdminActionDto {
  /** Matches ActionDefDto.source — identifies which ActionDef this action belongs to */
  source: string;
  /** ActionGroup identifier (Group 1 = Goals, Group 2 = Concerns/Plan, Group 3 = MVar-linked, Group 4 = Provider) */
  group: string;
  /** ActionMVar identifier — links to a specific concern mvarname for Groups 2 & 3 */
  mvar: string;
  /** ActionType identifier within the ActionGroup */
  action: string;
  /** Planned due date (ISO string or display date string) */
  dueDate: string;
  /** Where the action takes place */
  location: string;
  /** Recurrence / schedule descriptor (e.g. 'Daily', 'Weekly') */
  schedule: string;
  /** Actual date completed or actual outcome recorded */
  actual: string;
  /** Expense level associated with this action */
  expense: string;
  /** Importance rating for this action */
  importance: string;
  /** Outcome recorded after the action is carried out */
  outcome: string;
  /** ISO timestamp when this action instance was created */
  created: string;
  /** ISO timestamp when this action instance was completed */
  completed: string;
  /** Current status of the action (e.g. 'pending', 'in-progress', 'completed', 'cancelled') */
  status: string;
  /** Display sort order across all selected actions (backend DTO stores this as string) */
  rank?: number | string;
}

/**
 * App Profile PHI (Protected Health Information) DTO
 */
export interface AppProfilePHIDto {
  dname: string;      // Display Name
  fname: string;      // First Name
  lname: string;      // Last Name
  email: string;      // Email
  dob: string;        // Date of Birth (ISO string)
  gender: string;     // Gender
  extra: MVarDataDto[];  // Additional data fields
}

/**
 * Profile Admin DTO for admin segment data
 */
export interface ProfileAdminDto {
  recId: string;      // Record ID
  recDate: string;    // Record Date (ISO string)
  data: MVarDataDto[];   // Admin data
}

/**
 * Connection DTO — matches C# ConnectionDto in AppProfileDto
 */
export interface ProfileConnectionDto {
  ConnId: string;
  DName: string;
  Email: string;
  Type: string;        // 'Linked' | 'Secure Email'
  Permissions: string; // 'Full Access' | 'Assessments & Reports' | 'Notifications Only'
  Primary: string;
  Status: string;
}

/**
 * Complete App Profile DTO with hierarchical structure
 * This matches the backend C# AppProfileDto structure
 */
export interface AppProfileDto {
  profileId: string;
  accountId: string;
  phi: AppProfilePHIDto;
  milestones: MVarDataDto[];
  concerns: MVarDataDto[];
  goals: MVarDataDto[];
  actions: AdminActionDto[];
  adminstates: AdminStateDto[];  // Admin assessment states matching on profileId
  domainstate: MVarDataDto[];  // Domain development data (devFloor, devCeiling, devAge, etc.)
  demogs: MVarDataDto[];        // Demographic information
  medical: MVarDataDto[];       // Medical information
  wellchecks: WellCheckDto[];   // Well-check visit state records
  growth: GrowthTrackDto;        // Growth measurement history
  vaccines: VaccineTrackDto;     // Vaccine administration history
  prescriptions: PrescriptionTrackDto; // Prescription history
  referrals: ReferralTrackDto;   // Referral history
  Prescriptions?: PrescriptionTrackDto; // PascalCase alias for backend compatibility
  Referrals?: ReferralTrackDto;   // PascalCase alias for backend compatibility
  /** Admin history items embedded in the profile response (C# List<AdminHistoryGetListItemDto>). */
  adminhistory?: any[];
  modified: string;   // DateTime as ISO string
  /** Profile status: '1' = active, '0' = disabled, '-1' = deleted */
  status?: string;
}

/**
 * App Profile query parameters for backend operations
 */
export interface AppProfileQuery {
  profileId: string;
  accountId: string;
}

/**
 * Helper functions for AppProfileDto operations
 */

/**
 * Create a new empty AppProfileDto
 */
export const createEmptyAppProfileDto = (profileId: string, accountId: string): AppProfileDto => ({
  profileId,
  accountId,
  phi: {
    dname: '',
    fname: '',
    lname: '',
    email: '',
    dob: '',
    gender: '',
    extra: []
  },
  milestones: [],
  concerns: [],
  goals: [],
  actions: [],
  adminstates: [],
  domainstate: [],
  demogs: [],
  medical: [],
  wellchecks: [],
  growth: { data: [] },
  vaccines: { events: [] },
  prescriptions: { events: [] },
  referrals: { events: [] },
  modified: new Date().toISOString(),
  status: '1',
});

/**
 * Update the modified timestamp
 */
export const updateAppProfileModified = (profile: AppProfileDto): AppProfileDto => ({
  ...profile,
  modified: new Date().toISOString()
});

/**
 * Create MVar data entry
 */
export const createMVarData = (name: string, value: string): MVarDataDto => ({
  name,
  value
});

/**
 * Create Profile Admin entry
 */
export const createProfileAdmin = (recId: string, data: MVarDataDto[]): ProfileAdminDto => ({
  recId,
  recDate: new Date().toISOString(),
  data
});

/**
 * Update or add MVar data in a collection
 */
export const updateMVarData = (
  collection: MVarDataDto[], 
  name: string, 
  value: string
): MVarDataDto[] => {
  const existingIndex = collection.findIndex(item => item.name === name);
  
  if (existingIndex >= 0) {
    // Update existing
    const updated = [...collection];
    updated[existingIndex] = { name, value };
    return updated;
  } else {
    // Add new
    return [...collection, { name, value }];
  }
};

/**
 * Remove MVar data from a collection
 */
export const removeMVarData = (
  collection: MVarDataDto[], 
  name: string
): MVarDataDto[] => {
  return collection.filter(item => item.name !== name);
};

/**
 * Get MVar data value by name
 */
export const getMVarDataValue = (
  collection: MVarDataDto[], 
  name: string
): string | null => {
  const item = collection.find(item => item.name === name);
  return item ? item.value : null;
};